import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Sparkles, ChevronDown } from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Pricing.css';
import { PricingHero } from '../components/PageHeroes';
import { useTheme } from '../context/ThemeContext';

const Pricing = () => {
  const { isDarkMode } = useTheme();
  const [openFaq, setOpenFaq] = useState(null);

  const plans = [
    {
      name: 'Starter',
      price: '10',
      period: 'per month',
      description: 'Core tools for small teams',
      features: [
        'Payroll & HR (leave, recruitment, onboarding)',
        'Employee self-service',
        'Invoicing & customer management',
        'Accounts payable & expense claims',
        'Banking & reconciliation',
        'Email support'
      ],
      highlighted: false
    },
    {
      name: 'Professional',
      price: '30',
      period: 'per month',
      description: 'For businesses managing their own books',
      features: [
        'Everything in Starter',
        'Full accounting & general ledger',
        'VAT returns & filing',
        'Appointment scheduling',
        'Document management & sign-off',
        'Priority email & phone support'
      ],
      highlighted: true
    },
    {
      name: 'Business',
      price: '50',
      period: 'per month',
      description: 'The complete platform',
      features: [
        'Everything in Professional',
        'Custom approval workflows',
        'Email marketing campaigns',
        'Advanced financial reporting',
        'Priority support with SLA',
        'Dedicated onboarding'
      ],
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes! You can change your plan at any time. Changes take effect immediately.'
    },
    {
      question: 'Is there a free trial?',
      answer: "Yes — every plan includes full access to every feature for 14 days before billing starts, no credit card required. You choose which plan you'll move to afterward."
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept bank transfer for all plans, with card payments coming soon for UK businesses.'
    },
    {
      question: 'Is there a contract?',
      answer: "No contracts required. Cancel anytime, though we'd love to keep you as a customer!"
    }
  ];

  // Dark-mode-aware palette for the pricing cards — kept local to this component
  // rather than relying on CSS classes/tokens, since those have repeatedly been
  // overridden by other rules (gradient-text headings, stale cache, etc.) elsewhere
  // on this page tonight. Reading isDarkMode directly guarantees this always renders
  // correctly regardless of what any external stylesheet does.
  const palette = {
    cardBg: isDarkMode ? '#161b22' : 'white',
    cardBorder: isDarkMode ? '#30363d' : '#e2e8f0',
    highlightBorder: '#0ea5e9',
    heading: isDarkMode ? '#e6edf3' : '#0f172a',
    subtext: isDarkMode ? '#8b949e' : '#64748b',
    price: isDarkMode ? '#e6edf3' : '#0f172a',
    divider: isDarkMode ? '#30363d' : '#e2e8f0',
    featureDivider: isDarkMode ? '#21262d' : '#f1f5f9',
    featureText: isDarkMode ? '#e6edf3' : '#1e293b',
    check: '#0ea5e9',
    btnDefaultBg: isDarkMode ? '#21262d' : '#0f172a',
    btnDefaultText: 'white',
  };

  return (
    <div className="page-container">
      <PricingHero />

      <section className="section-pricing" style={{ paddingBottom: 0 }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
              borderRadius: '1rem',
              padding: '1.25rem 1.75rem',
              marginBottom: '2.5rem',
              boxShadow: '0 8px 24px rgba(14, 165, 233, 0.25)',
              flexWrap: 'wrap',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '0.6rem',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Sparkles size={18} color="white" />
            </div>
            <span style={{ color: 'white', fontSize: '0.98rem', lineHeight: 1.5 }}>
              <strong>Every plan starts with a free 14-day trial</strong> — full access to every feature, no credit card required.
            </span>
          </motion.div>
        </div>
      </section>

      <section className="section-pricing" style={{ paddingTop: '1rem', paddingBottom: '4rem' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              alignItems: 'stretch',
            }}
          >
            {plans.map((plan, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                style={{
                  background: palette.cardBg,
                  border: plan.highlighted ? `2px solid ${palette.highlightBorder}` : `1px solid ${palette.cardBorder}`,
                  borderRadius: '1rem',
                  padding: '2rem',
                  boxShadow: plan.highlighted
                    ? '0 12px 32px rgba(14, 165, 233, 0.2)'
                    : isDarkMode
                      ? '0 4px 16px rgba(0, 0, 0, 0.3)'
                      : '0 4px 16px rgba(15, 23, 42, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {plan.highlighted && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-0.75rem',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#0ea5e9',
                      color: 'white',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      padding: '0.3rem 0.9rem',
                      borderRadius: '999px',
                    }}
                  >
                    Most Popular
                  </span>
                )}

                <div style={{ marginBottom: '1.25rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: palette.heading }}>{plan.name}</h3>
                  <p style={{ margin: '0.35rem 0 0', color: palette.subtext, fontSize: '0.9rem' }}>{plan.description}</p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <span style={{ fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: palette.price }}>£{plan.price}</span>
                  <span style={{ fontSize: '0.9rem', color: palette.subtext, marginLeft: '0.4rem' }}>{plan.period}</span>
                </div>

                <motion.a
                  href="/signup"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    background: plan.highlighted ? '#0ea5e9' : palette.btnDefaultBg,
                    color: palette.btnDefaultText,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    padding: '0.85rem',
                    borderRadius: '0.65rem',
                    textDecoration: 'none',
                    marginBottom: '2rem',
                  }}
                >
                  Start free trial
                  <ArrowRight size={18} />
                </motion.a>

                <div
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: palette.subtext,
                    borderBottom: `1px solid ${palette.divider}`,
                    paddingBottom: '0.75rem',
                    marginBottom: '0.75rem',
                  }}
                >
                  What's included
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {plan.features.map((feature, fidx) => (
                    <div
                      key={fidx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.6rem',
                        padding: '0.65rem 0',
                        borderTop: fidx === 0 ? 'none' : `1px solid ${palette.featureDivider}`,
                        fontSize: '0.9rem',
                        color: palette.featureText,
                      }}
                    >
                      <Check size={16} style={{ color: palette.check, flexShrink: 0, marginTop: '2px' }} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-features bg-light">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid pricing-faq-list">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <motion.div
                  key={idx}
                  className={`faq-item pricing-faq-item ${isOpen ? 'is-open' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <button
                    className="pricing-faq-question"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                  >
                    <h4>{faq.question}</h4>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ display: 'flex', flexShrink: 0 }}
                    >
                      <ChevronDown size={20} />
                    </motion.span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={isOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p style={{ paddingTop: '0.5rem' }}>{faq.answer}</p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to get started?</h2>
            <p>Start your 14-day free trial today. No credit card required.</p>
            <motion.a
              href="/signup"
              className="btn btn-primary btn-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Free Trial
              <ArrowRight size={18} />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;